import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Slider from '@mui/material/Slider';
import Tooltip from '@mui/material/Tooltip';
import { ThemeProvider, createTheme } from '@mui/material/styles';

import { useAppDispatch, useAppSelector } from 'state/hooks';

import { ArrowButton, PlayButton } from 'features/common/UIHelpers';
import {
  chart_background_color,
  null_grey,
  secondary_light_text_color,
  stl_background_color,
  stl_light_background_color,
  stl_light_grey,
  stl_realtime_color,
  stl_realtime_color_light_desat,
  transparent_null_grey,
  workspace_color,
} from 'theme/cemTheme';

import { useCallbackWithErrorHandling } from '../../app/ErrorHandling';
import {
  selectRepresentedSlots,
  selectVnrtData,
} from '../../state/realtimeSlices';
import {
  selectComparison,
  selectProject,
  selectShortestAvailableBin,
  selectTargetDate,
  selectTimeOfDay,
  selectTimelineMetric,
  setTimeOfDay,
  setTimelineMetric,
} from '../../state/workflowSlice';
import { ColorGradient, timeline_bar_gradient } from '../chart/colorMaps';
import { formatTimeOfDay, nowUnix } from '../common/datetimeUtils';
import {
  minutes_to_slots,
  slots_to_minutes,
  ts_to_minutes,
} from '../common/slotsUtils';
import { MapContext } from '../map/MapContext';
import { useNextLastDay } from '../workflow_nrt/useNextLastDay';
import { getMinuteTicker, useRealtimeData } from './RealtimeDataProvider';
import { useActivityTimeseries } from './activityTimeseries';
import {
  TL_METRICS_OPTIONS_RT_EQUIVALENT,
  TL_METRICS_OPTIONS_VNRT,
  TL_METRICS_OPTIONS_VNRT_EQUIVALENT,
  isRealtimeMetric,
  isVNRTMetric,
} from './metricsOptions';

import styles from './WorkflowTimeline.module.css';

const timeMin = -3 * 60;
const timeMax = (24 + 3) * 60;
const kPlayTickMinimumMillis = 400;
const WIDTH = '1.5rem';
const HEIGHT = '1rem';

export const timeSliderTheme = createTheme({
  components: {
    MuiSlider: {
      styleOverrides: {
        root: {
          disableRipple: true,
        },
        thumb: {
          borderRadius: '4px',
          height: HEIGHT,
          width: WIDTH,
          background: 'none',
          boxShadow: '0px 0px 15px 0px #00000080 !important',
          ':hover': {
            boxShadow: '0px 0px 15px 0px #00000080',
          },
          '::after': {
            border: `2px ${secondary_light_text_color} solid`,
            borderRadius: '4px',
            zIndex: 100,
            height: HEIGHT,
            width: WIDTH,
            background: 'none',
            boxShadow: '0 3px 20px 0 rgba(16, 16, 16, 0.30)',
          },
        },
        rail: {
          height: '8px',
          borderRadius: '4px',
          backgroundColor: stl_light_background_color,
          opacity: 0.8,
        },
        track: {
          height: '30px',
          background: 'none',
          border: 'none',
        },
        mark: {
          height: WIDTH,
          borderRadius: '1px',
          backgroundColor: 'var(--primary-text)',
          opacity: 0.7,
          border: '1px solid black',
        },
        markActive: {
          backgroundColor: stl_light_grey,
        },
        markLabel: {
          color: 'var(--primary-text)',
        },
        valueLabel: {
          marginTop: '1px',
          background: `linear-gradient(to left, ${workspace_color}00, ${workspace_color}, ${workspace_color}, ${workspace_color}00)`,
          width: '20em',
          color: 'var(--primary-text)',
          transform: 'translateY(33px) !important',
          padding: 0,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          color: 'var(--primary-text)',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          alignItems: 'start !important',
        },
        item: {
          paddingTop: '0',
        },
      },
    },
  },
});

export function TimeSlider() {
  const timeOfDay = useAppSelector(selectTimeOfDay);
  const binSize = useAppSelector(selectShortestAvailableBin);
  const binCount = binSize ? ((3 + 24 + 3) * 60) / binSize : 1;
  const timelineMetric = useAppSelector(selectTimelineMetric);
  const targetDate = useAppSelector(selectTargetDate);
  const comparison = useAppSelector(selectComparison);
  const project = useAppSelector(selectProject);
  const slotsRepresented = useAppSelector(selectRepresentedSlots);
  const vnrtData = useAppSelector(selectVnrtData);
  const dispatch = useAppDispatch();
  const [playing, setPlaying] = useState(false);
  const refPlaying = useRef(playing);
  const refLastPlayPaint = useRef(undefined);
  const refTimeOfDay = useRef(timeOfDay);
  refTimeOfDay.current = timeOfDay;
  const { nextDayAvailable, lastDayAvailable } = useNextLastDay();
  const { refMap, mapReady } = useContext(MapContext);

  const tt = getMinuteTicker();
  const { realtime: realtimeData, is_complete_vnrt } = useRealtimeData(tt);

  const maxRepresentedBin = useMemo(
    () => Math.max(...Object.keys(slotsRepresented).map(Number)),
    [slotsRepresented],
  );

  const { timeseries } = useActivityTimeseries();
  const colorMap = new ColorGradient(
    timeline_bar_gradient(0, 100).toInterleaved(),
  );
  const railBackgroundMemo = useMemo(() => {
    let railBackground;
    if (timeseries && !TL_METRICS_OPTIONS_VNRT.includes(timelineMetric)) {
      railBackground = 'linear-gradient(0.25turn';
      const gradient_chunk = (color, i) =>
        `, ${color} ${(100 * (i / timeseries.length)).toFixed(3)}%`;
      for (let i = 0; i < timeseries.length; i++) {
        const min = slots_to_minutes(i, binSize);
        const value = timeseries[i];
        console.assert(value <= 1 || Number.isNaN(value), value, timeseries, i);
        let color;
        if (
          // If the previous day isn't available, make it background color to indicate it's not selectable
          !lastDayAvailable &&
          min < 0
        ) {
          color = stl_background_color;
          railBackground += gradient_chunk(color, i);
          railBackground += gradient_chunk(color, i + 0.99);
        } else if (
          // If the next day isn't available, make it background color to indicate it's not selectable
          !nextDayAvailable &&
          min > 24 * 60
        ) {
          color = stl_background_color;
          railBackground += gradient_chunk(color, i - 0.99);
          railBackground += gradient_chunk(color, i);
        } else {
          if (!Number.isNaN(value)) {
            color = colorMap.sample(value * 100);
          } else {
            color = null_grey;
          }

          railBackground += gradient_chunk(color, i);
        }
      }
      railBackground += ');';
    } else if (is_complete_vnrt && Object.keys(slotsRepresented).length === 0) {
      railBackground = transparent_null_grey;
    } else if (Object.keys(slotsRepresented).length > 0) {
      railBackground = 'linear-gradient(0.25turn';
      const gradient_chunk = (color, offset) =>
        `, ${color} ${(100 * offset).toFixed(3)}%`;
      let state;
      for (let i = 0; i < binCount; i++) {
        let value = slotsRepresented[i.toString()] ? 1 : 0;
        value = value || vnrtData[i.toString()] ? 1 : 0;
        if (value !== state) {
          const offset = Math.max(i - 0.5, 0) / binCount;
          if (state !== undefined) {
            const color = state
              ? stl_realtime_color_light_desat
              : transparent_null_grey;
            railBackground += gradient_chunk(color, offset - 0.001);
          }
          const color = value
            ? stl_realtime_color_light_desat
            : transparent_null_grey;
          railBackground += gradient_chunk(color, offset);
        }
        state = value;
      }
      railBackground += gradient_chunk(
        state ? stl_realtime_color_light_desat : transparent_null_grey,
        1,
      );
      railBackground += ');';
    }

    return railBackground;
  }, [
    timeseries,
    binSize,
    binCount,
    slotsRepresented,
    vnrtData,
    targetDate,
    timelineMetric,
    is_complete_vnrt,
    lastDayAvailable,
    nextDayAvailable,
  ]);

  function time_of_day_surpases_rt(value) {
    if (realtimeData) {
      const realtimeMinOffset = ts_to_minutes(
        realtimeData.timestamp,
        targetDate,
      );
      if (value >= realtimeMinOffset) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  const constrainSliderToRealtime = () => {
    if (realtimeData) {
      const offset = ts_to_minutes(realtimeData.timestamp, targetDate);
      // console.log('Setting time of day to ', offset, t);
      if (offset <= timeMax) {
        dispatch(setTimeOfDay(offset));
      } else {
        dispatch(
          setTimelineMetric(TL_METRICS_OPTIONS_VNRT_EQUIVALENT[timelineMetric]),
        );
      }
    }
  };

  function constrainSliderToBins(newValue) {
    if (binSize) {
      const newToD = binSize * Math.floor(newValue / binSize); // Round to bin
      if (newToD !== timeOfDay) {
        if (newToD !== newValue) {
          console.log(
            'Constraining time of day from a -> b',
            timeOfDay,
            newToD,
          );
        }
        dispatch(setTimeOfDay(newToD));
      }
    }
  }

  function getTodConstrainedToAvailableDays(time) {
    let newTimeOfDay = time;
    newTimeOfDay = Math.min((24 + 3) * 60 - binSize, newTimeOfDay);
    if (!lastDayAvailable && time < 0) {
      newTimeOfDay = Math.max(0, newTimeOfDay);
    }
    if (!nextDayAvailable) {
      newTimeOfDay = Math.min(24 * 60, newTimeOfDay);
    }
    return newTimeOfDay;
  }

  function constrainSliderToAppropriate() {
    if (isRealtimeMetric(timelineMetric)) {
      constrainSliderToRealtime();
    } else if (isVNRTMetric(timelineMetric)) {
      let newTimeOfDay = timeOfDay;
      if (!is_complete_vnrt && realtimeData) {
        // If this is a day that supports Realtime, but we're in a VNRT metric
        if (time_of_day_surpases_rt(timeOfDay)) {
          // If the time slider is set above RT
          newTimeOfDay = ts_to_minutes(realtimeData.timestamp, targetDate) - 15; // Set the time slider to be at the RT position
        }
      }
      constrainSliderToBins(newTimeOfDay);
    } else {
      constrainSliderToBins(getTodConstrainedToAvailableDays(timeOfDay));
    }
  }

  const changeSliderTime = (newValue: number) => {
    console.assert(newValue >= timeMin && newValue <= timeMax);
    dispatch(setTimeOfDay(getTodConstrainedToAvailableDays(newValue)));
    if (TL_METRICS_OPTIONS_VNRT.includes(timelineMetric)) {
      if (is_complete_vnrt) {
        dispatch(
          setTimelineMetric(
            TL_METRICS_OPTIONS_VNRT_EQUIVALENT[timelineMetric] ||
              timelineMetric,
          ),
        );
        constrainSliderToBins(newValue);
      } else {
        const hasVnrtData = Object.keys(vnrtData).length > 0;
        if (hasVnrtData || realtimeData) {
          if (time_of_day_surpases_rt(newValue)) {
            if (isVNRTMetric(timelineMetric)) {
              dispatch(
                setTimelineMetric(
                  TL_METRICS_OPTIONS_RT_EQUIVALENT[timelineMetric],
                ),
              );
            }
            constrainSliderToRealtime();
          } else if (isRealtimeMetric(timelineMetric)) {
            dispatch(
              setTimelineMetric(
                TL_METRICS_OPTIONS_VNRT_EQUIVALENT[timelineMetric],
              ),
            );
            constrainSliderToBins(newValue);
          } else {
            constrainSliderToBins(newValue);
          }
        }
      }
    }
    // TODO Space these so only once every 200ms
  };

  const handleSliderChange = (_e: Event, newValue: number) => {
    changeSliderTime(newValue);
  };

  useEffect(() => {
    // This handles date change, either from a user changing date or by hitting RT, thus it has to not force RT -> VNRT
    if (TL_METRICS_OPTIONS_VNRT.includes(timelineMetric)) {
      if (is_complete_vnrt) {
        // Switch to vnrt from rt if needs be
        dispatch(
          setTimelineMetric(
            TL_METRICS_OPTIONS_VNRT_EQUIVALENT[timelineMetric] ||
              timelineMetric,
          ),
        );
        constrainSliderToBins(timeOfDay);
      } else {
        // Otherwise lets not change metric, just fix up the ol' time slider
        constrainSliderToAppropriate();
      }
    }
  }, [targetDate, is_complete_vnrt]);

  useEffect(() => {
    constrainSliderToAppropriate();
  }, [realtimeData, vnrtData]);

  useEffect(() => {
    constrainSliderToAppropriate();
  }, [timelineMetric]);

  useEffect(() => {
    if (slotsRepresented && Object.keys(slotsRepresented).length > 0) {
      if (minutes_to_slots(timeOfDay, binSize) > maxRepresentedBin) {
        dispatch(setTimeOfDay(slots_to_minutes(maxRepresentedBin, binSize)));
      }
    }
  }, [slotsRepresented]);

  function playingCallback() {
    if (refPlaying.current) {
      if (refTimeOfDay.current < timeMax) {
        const millisToReady = refLastPlayPaint.current
          ? kPlayTickMinimumMillis - (nowUnix() - refLastPlayPaint.current)
          : undefined;
        if (!millisToReady || millisToReady < 10) {
          changeSliderTime(Math.min(refTimeOfDay.current + binSize, timeMax));
          refLastPlayPaint.current = nowUnix();
        } else {
          console.log(`Delaying repaint by ${millisToReady}ms`);
          setTimeout(playingCallback, millisToReady);
        }
      } else {
        setPlaying(false);
      }
    }
  }

  useEffect(() => {
    refPlaying.current = playing;
    if (playing && mapReady) {
      refMap.current.on('styledata', playingCallback);
      playingCallback();
    } else {
      refMap.current?.off('styledata', playingCallback);
    }
    return () => {
      refMap.current?.off('styledata', playingCallback);
    };
  }, [playing, mapReady]);

  useEffect(() => {
    if (playing) {
      setPlaying(false);
    }
  }, [targetDate, comparison, project]);

  const marks = [
    {
      value: 0,
      label: '00:00',
    },
    {
      value: 12 * 60,
      label: '12:00',
    },
    {
      value: 24 * 60,
      label: '00:00',
    },
  ];
  let railBackground;
  if (!railBackgroundMemo) {
    railBackground = `${chart_background_color} !important`;
  } else {
    railBackground = railBackgroundMemo;
  }

  const isRealtime = isRealtimeMetric(timelineMetric);

  return (
    <ThemeProvider theme={timeSliderTheme}>
      <Box>
        <Grid container spacing={0} alignItems="center">
          <Grid item>
            <Tooltip title="Go back a time step">
              <ArrowButton
                forward={false}
                disabled={timeOfDay === timeMin}
                handleClick={() =>
                  changeSliderTime(Math.max(timeOfDay - binSize, timeMin))
                }
              />
            </Tooltip>
          </Grid>
          <Grid item xs>
            <Slider
              value={timeOfDay}
              onChange={useCallbackWithErrorHandling(handleSliderChange)}
              aria-labelledby="threshold-slider"
              getAriaValueText={formatTimeOfDay}
              valueLabelFormat={formatTimeOfDay}
              className={isRealtime ? styles.realtime_slider_thumb : undefined}
              valueLabelDisplay="on"
              min={timeMin}
              max={timeMax}
              step={binSize}
              color="secondary"
              marks={marks}
              sx={{
                '.MuiSlider-rail': {
                  background: railBackground,
                },
                '.MuiSlider-thumb': {
                  background: isRealtime ? stl_realtime_color : undefined,
                  borderColor: isRealtime ? stl_realtime_color : undefined,
                  // animation: isRealtime ? `${styles.realtime_thumb_pulse} 2s infinite !important` : undefined,
                },
                '.MuiSlider-thumb::after': {
                  border: isRealtime
                    ? `3px ${stl_realtime_color} solid`
                    : '3px white solid',
                  animation: isRealtime
                    ? `${styles.realtime_thumb_pulse} 2s infinite`
                    : undefined,
                },
              }}
            />
          </Grid>
          <Grid item>
            <Tooltip title="Go forward a time step">
              <ArrowButton
                handleClick={() =>
                  changeSliderTime(Math.min(timeOfDay + binSize, timeMax))
                }
                disabled={timeOfDay === timeMax}
              />
            </Tooltip>
          </Grid>
          <Grid item>
            <PlayButton
              disabled={isRealtimeMetric(timelineMetric)}
              playing={playing}
              setPlaying={setPlaying}
              key={Number(playing)}
            />
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
}
